import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

import DatagridPremiumUi from '../../Interface/DatagridPremmium/DataGridPremiumUI';
import { dataGridPremiumColumns } from '../../../utils/helper';
import { DataGridIdentifiers } from '../../Interface/Datagrid/DataGridIdentifiers';
import { useLoader } from '../../../provider/LoaderProvider';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import { DocumentType } from '../../../config/DocumentType';
import { WmsType } from '../../../config/wms/wmsType';
import wmsBaseService from '../../../service/wms/wmsBase.service';
import RecognizeItemButton from '../Components/RecognizeItemButton';
import WarehouseField from '../Components/WarehouseField';

const DeliveryDocsTab = ({ autocompleteData, autocompleteOptions }) => {
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState({ rows: [], rowCount: 0 });
  const [selectedWhsCode, setSelectedWhsCode] = useState('');

  const loadReports = async () => {
    setLoading(true);
    showLoader();
    try {
      const data = await wmsBaseService.getOpenedDocs(DocumentType.CustomerOrder);
      setReports({
        rows: Array.isArray(data?.rows) ? data.rows : [],
        rowCount: data?.rowCount ?? 0,
      });
    } catch (e) {
      addNotification({ content: 'A bizonylatok betöltése sikertelen.', type: NotificationType.ERROR });
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  useEffect(() => {
    loadReports();
  }, []);

  const handleCreateWms = useCallback(
    async (row) => {
      showLoader();
      try {
        const response = await wmsBaseService.createData({
          wmsType: WmsType.Delivery,
          targetDocType: DocumentType.Delivery,
          companyId: row?.companyId ?? null,
          whsCode: selectedWhsCode || row?.whsCode || null,
          baseId: row?.docId ?? row?.id ?? null,
          baseDocNum: row?.docNum ?? null,
          baseDocType: row?.docTypeId ?? DocumentType.CustomerOrder,
        });
        navigate(`../deliveryDoc/${response.id}`);
        addNotification({ content: 'WMS fej sikeresen létrehozva.', type: NotificationType.SUCCESS });
      } catch (e) {
        addNotification({ content: 'WMS fej létrehozása sikertelen.', type: NotificationType.ERROR });
      } finally {
        hideLoader();
      }
    },
    [hideLoader, navigate, selectedWhsCode, showLoader]
  );

  const columns = useMemo(
    () =>
      dataGridPremiumColumns([
        {
          field: 'docNum',
          headerName: 'Bizonylatszám',
          flex: 0.8,
          renderCell: (params) => (
            <Box sx={{ py: 0.5 }}>
              <Box>{params.row?.docNum ?? ''}</Box>
              <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{params.row?.whsCode ?? ''}</Box>
            </Box>
          ),
        },
        {
          field: 'companyName',
          headerName: 'Partner',
          flex: 0.8,
          renderCell: (params) => `${params.row?.companyCode ?? ''} - ${params.row?.companyName ?? ''}`.trim(),
        },
        {
          field: 'dueDate',
          headerName: 'Határidő',
          type: 'date',
          flex: 0.8,
          valueGetter: (_data, row) => (row?.dueDate ? new Date(row.dueDate) : null),
          renderCell: (params) => (params.row?.dueDate ? dayjs(params.row.dueDate).format('YYYY.MM.DD') : ''),
        },
        {
          field: 'operators',
          headerName: '',
          sortable: false,
          filterable: false,
          flex: 0.35,
          renderCell: (params) => (
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}
            >
              <RecognizeItemButton onClick={() => handleCreateWms(params.row)} />
            </Box>
          ),
        },
      ]),
    [handleCreateWms]
  );

  return (
    <Box sx={{ pb: 2, pt: 3, px: 0, width: '100%' }}>
      <Box sx={{ pb: 2, px: 1, maxWidth: 360 }}>
        <WarehouseField
          value={selectedWhsCode}
          onChange={(nextValue) => setSelectedWhsCode(nextValue ?? '')}
          warehouseData={autocompleteData?.warehouse}
          warehouseOptions={autocompleteOptions?.warehouse}
        />
      </Box>

      <DatagridPremiumUi
        datagridIdentifier={DataGridIdentifiers.wmsDeliveryDocs}
        rows={reports.rows}
        rowCount={reports.rowCount}
        columns={columns}
        loading={loading}
        sx={{ width: '100%' }}
        getRowHeight={() => 'auto'}
        headerFilters={false}
        getRowId={(row) => row.docId}
      />
    </Box>
  );
};

export default DeliveryDocsTab;
